import { InertiaLinkProps, Link } from '@inertiajs/react';
import { cn } from '@Pages/pageRuntime/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import * as React from 'react';

/**
 * LinkBox wraps a container and automatically elevates all nested interactive
 * elements (links, buttons) above the LinkOverlay so they remain clickable.
 * Use with `asChild` to merge props onto your own element (e.g. a TableRow).
 *
 * @see https://chakra-ui.com/docs/components/link-overlay for the original pattern.
 */
interface LinkBoxProps extends React.HTMLAttributes<HTMLElement> {
  asChild?: boolean;
}

const LinkBox = ({
  className,
  asChild,
  ref,
  ...props
}: LinkBoxProps & { ref?: React.Ref<HTMLElement> }) => {
  const Comp = asChild ? Slot : 'div';
  return (
    <Comp
      ref={ref as React.Ref<HTMLDivElement>}
      className={cn(
        'relative [&_a:not(.link-overlay)]:relative [&_a:not(.link-overlay)]:z-10 [&_button]:relative [&_button]:z-10',
        className
      )}
      {...props}
    />
  );
};
LinkBox.displayName = 'LinkBox';

/**
 * LinkOverlay renders an Inertia Link whose ::before pseudo-element stretches
 * to fill the nearest positioned ancestor (the LinkBox). Clicking anywhere
 * inside the LinkBox that isn't an elevated interactive element triggers this link.
 */
const LinkOverlay = ({
  className,
  ref,
  ...props
}: InertiaLinkProps & { ref?: React.Ref<HTMLAnchorElement> }) => (
  <Link
    ref={ref}
    className={cn('link-overlay static before:absolute before:inset-0 before:z-0', className)}
    {...props}
  />
);
LinkOverlay.displayName = 'LinkOverlay';

export { LinkBox, LinkOverlay };
